import type { ChipLink } from "@/components/search/plp-seo/LinkChips";
import {
  ICON_GUIDES_BY_BRAND,
  type IconGuide,
  type IconGuideLocale,
} from "@/data/icon-guides-by-brand";
import { toContentLocale } from "@/i18n/config";

/** The brand overview segment per locale, as in `BRAND_OVERVIEW_LOCALIZED_BASE_PATH_MAP`. */
const BRAND_SEGMENTS = [
  "brands",
  "merken",
  "marken",
  "marques",
  "marcas",
  "marche",
];

const BRAND_PAGE_PATH = new RegExp(
  `^/(?:${BRAND_SEGMENTS.join("|")})/([a-z0-9-]+)/?$`
);

/** The brand slug of a top-level brand listing page path (no locale prefix), else null. */
export const brandSlugFromPagePath = (
  pagePath: string | null | undefined
): string | null => BRAND_PAGE_PATH.exec(pagePath ?? "")?.[1] ?? null;

const BRAND_CATEGORY_PAGE_PATH = new RegExp(
  `^/(?:${BRAND_SEGMENTS.join("|")})/([a-z0-9-]+)/([a-z0-9-]+)/?$`
);

/**
 * The brand slug and localized category segment of a brand x category listing page path (no
 * locale prefix): `/merken/ligne-roset/banken` -> `{ brand: "ligne-roset", category: "banken" }`.
 */
export const brandCategoryFromPagePath = (
  pagePath: string | null | undefined
): { brand: string; category: string } | null => {
  const match = BRAND_CATEGORY_PAGE_PATH.exec(pagePath ?? "");

  const brand = match?.[1];
  const category = match?.[2];

  return brand && category ? { brand, category } : null;
};

const toChipLinks = (
  locale: string | undefined,
  guides: IconGuide[]
): ChipLink[] => {
  const loc: IconGuideLocale = toContentLocale(locale);
  const prefix = loc === "en" ? "" : `/${loc}`;

  return guides.flatMap(guide => {
    const title = guide.title[loc];

    return title ? [{ href: `${prefix}/icon/${guide.slug}`, title }] : [];
  });
};

/**
 * WHO-5764: links to the price and authenticity guides on the brand's icon pages, titled in
 * the page language. Non-EN hrefs carry the locale prefix, like the editorial links. A brand
 * page links every guide of the brand; a brand x category page (WHO-6195) only the guides
 * whose `categorySegments` hold its category. Empty for other pages or brands without a guide.
 */
export const getIconGuideLinks = (
  locale: string | undefined,
  pagePath: string | null | undefined
): ChipLink[] => {
  const brand = brandSlugFromPagePath(pagePath);

  if (brand) {
    return toChipLinks(locale, ICON_GUIDES_BY_BRAND[brand] ?? []);
  }

  const brandCategory = brandCategoryFromPagePath(pagePath);
  const guides = brandCategory
    ? (ICON_GUIDES_BY_BRAND[brandCategory.brand] ?? []).filter(
        guide => guide.categorySegments?.includes(brandCategory.category)
      )
    : [];

  return toChipLinks(locale, guides);
};

/**
 * WHO-6196: the icon guides a product page links to, so a model page (the Togo, the Barcelona
 * chair) collects the links that today go to whichever single listing happens to rank. A guide
 * matches when the product is of the guide's brand and one of the titles contains one of its
 * `titleKeywords`. Titles are the translated and the original one.
 */
export const getIconGuideLinksForProduct = (
  locale: string | undefined,
  brandSlug: string | null | undefined,
  titles: (string | null | undefined)[]
): ChipLink[] => {
  const guides = brandSlug ? ICON_GUIDES_BY_BRAND[brandSlug] ?? [] : [];
  const haystack = titles.filter(Boolean).join(" ").toLowerCase();

  return toChipLinks(
    locale,
    guides.filter(
      guide => guide.titleKeywords?.some(keyword => haystack.includes(keyword))
    )
  );
};
